/**
 * Well-known application permissions and role definitions.
 * This abstraction decouples permission requirements from concrete role names.
 */

/** Frontend UX capabilities for implemented integrations; these are not backend claims. */
export const Permissions = {
  ClassroomsRead: "classrooms.read",
  StudentsRead: "students.read",
  SessionsJoin: "sessions.join",
  SessionsModerate: "sessions.moderate",
  PresentationsInsert: "presentations.insert",
  /**
   * Read the school-wide daily absence report (`GET /api/attendance/classrooms/daily-absence`).
   * The backend reserves this report for the tenant Admin role, so it is granted to `Admin` only.
   */
  AbsencesRead: "absences.read",
} as const;
export type KnownPermission = (typeof Permissions)[keyof typeof Permissions];
export type AppPermission = KnownPermission;

/**
 * Only roles emitted by the current login contract may be authored as requirements.
 *
 * `Admin` is the school admin: the backend mints it from a per-tenant `Admins` membership row
 * (`LoginCommand` + `Admin.RoleName`), exactly like Teacher and Student, so it is a real admin
 * rather than a front-end invention. It is a PER-SCHOOL authority, never a platform-wide operator.
 */
export const Roles = {
  Admin: "Admin",
  Teacher: "Teacher",
  Student: "Student",
} as const;
export type KnownSystemRole = (typeof Roles)[keyof typeof Roles];
export type AppRole = KnownSystemRole;
export type RolePermissionPolicy = Readonly<
  Record<AppRole, readonly AppPermission[]>
>;

/** Every role the application knows about; the allow-list for narrowing untrusted wire strings. */
export const KNOWN_ROLES: readonly AppRole[] = [
  Roles.Admin,
  Roles.Teacher,
  Roles.Student,
];

/** Narrows an untrusted role string from the token portfolio to a known role. */
export function isKnownRole(value: string): value is AppRole {
  return (KNOWN_ROLES as readonly string[]).includes(value);
}

/** Requirements evaluated by authorization guards and hooks. */
export interface AuthorizationRequirement {
  /** A single required permission (e.g. "students.read"). */
  requiredPermission?: AppPermission | undefined;
  /** Multiple permissions, all of which must be granted. */
  requiredPermissions?: readonly AppPermission[] | undefined;
  /** Multiple permissions, at least one of which must be granted. */
  anyPermission?: readonly AppPermission[] | undefined;
  /** A single required role (e.g. "Teacher"). */
  requiredRole?: AppRole | undefined;
  /** Multiple roles, at least one of which must match. */
  allowedRoles?: readonly AppRole[] | undefined;
}
